import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import { ROTAS } from '@/routes/routePaths';
import { auth } from '@/config/firebase.config';

const EmailValidatorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, logout, reenviarEmailValidacao } = useAuth();
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [tipoMensagem, setTipoMensagem] = useState<'success' | 'error'>('success');
  const [cooldown, setCooldown] = useState(0);

  const email = location.state?.email || usuario?.email;

  // Observador automático: verifica o status do e-mail a cada 5 segundos.
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (auth.currentUser) {
        handleVerificarValidacaoManual();
      }
    }, 35000);

    // Limpa o intervalo quando o componente é desmontado para evitar vazamento de memória.
    return () => clearInterval(intervalId);
  }, [navigate]);

  // Medida de segurança: redireciona se o usuário não deveria estar nesta página.
  useEffect(() => {
    if (!auth.currentUser && !location.state?.email) {
      navigate(ROTAS.LOGIN);
    } else {
      reenviarEmailValidacao();
    }
  }, [location.state, navigate]);

  // Temporizador de cooldown para o botão de reenviar e-mail.
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleReenviarEmail = async () => {
    setCarregando(true);
    setMensagem(null);
    try {
      await reenviarEmailValidacao();
      setCooldown(60);
      setTipoMensagem('success');
      setMensagem('Email de validação reenviado com sucesso! Verifique sua caixa de entrada.');
    } catch (error: any) {
      console.error('Erro ao reenviar email:', error);
      setTipoMensagem('error');
      if (error.code === 'auth/too-many-requests') {
        setMensagem('Muitas tentativas. Por favor, aguarde alguns minutos antes de tentar novamente.');
      } else {
        setMensagem('Ocorreu um erro ao reenviar o email.');
      }
    } finally {
      setCarregando(false);
    }
  };

  const handleVerificarValidacaoManual = async () => {
    if (!auth.currentUser) return;

    setCarregando(true);
    setMensagem(null);
    await auth.currentUser.reload();

    if (auth.currentUser.emailVerified) {
      setTipoMensagem('success');
      setMensagem('Email validado com sucesso! Redirecionando...');
      // O observador automático cuidará do redirecionamento em breve.
      window.location.reload(); // Força a atualização para refletir o estado autenticado
    } else {
      setTipoMensagem('error');
      setMensagem('O email ainda não foi validado. Verifique sua caixa de entrada e pasta de spam.');
    }
    setCarregando(false);
  };

  const fazerLogout = async () => {
    try {
      await logout();
      navigate(ROTAS.LOGIN);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed inset-0 z-0">
        <img src="/Background-secao2.png" alt="Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/20"></div>
      </div>
      {mensagem && (
        <Alert tipo={tipoMensagem} mensagem={mensagem} onClose={() => setMensagem(null)} className="mb-4 fixed left-0 top-10" />
      )}

      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-background/95 backdrop-blur-sm p-8 rounded-lg shadow-2xl max-w-md w-full border border-primary overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img src="/BG.png" alt="Background do card" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-black/5"></div>
        </div>

        <div className="relative z-20 -mt-10">
          <div className="flex justify-center mb-6">
            <Link to={'/'}>
              <img src={'/clube.png'} alt="TugasCoins Logo" className='h-64 object-contain' />
            </Link>
          </div>

          <h2 className="text-3xl font-bold -mt-20 text-center text-primary mb-4">
            Valide seu Email
          </h2>

          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="text-text-light mb-4">Enviamos um email de validação para:</p>
            <p className="text-primary font-semibold text-lg mb-4">{email || '...'}</p>
            <p className="text-text-light text-sm mb-2">Por favor, verifique sua caixa de entrada e clique no link de confirmação.</p>
            <p className="text-text-light text-xs italic">Não esqueça de verificar a pasta de spam!</p>
          </div>


          <div className="space-y-4">
            <Button
              tipo="primary"
              onClick={handleReenviarEmail}
              className="w-full py-3 text-lg font-semibold"
              disabled={carregando || cooldown > 0}
            >
              {cooldown > 0 ? `Aguarde ${cooldown}s` : 'Reenviar Email'}
            </Button>

            <Button
              tipo="secondary"
              onClick={handleVerificarValidacaoManual}
              className="w-full py-3 text-lg font-semibold"
              disabled={carregando}
            >
              {carregando ? 'Verificando...' : 'Já Validei, Verificar Agora'}
            </Button>

            <div className="pt-4 border-t border-gray-200">
              <Button tipo="outline" onClick={fazerLogout} className="w-full py-2 text-sm">
                Fazer Logout
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-text-light text-sm">
              Problemas com a validação?{' '}
              Entre em contato com o suporte - +55 32 99125-7255
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailValidatorPage;